import Interactable from 'Component/Interactable';
import Renderable from 'Component/Renderable';
import Game from 'Game';
import GameObject, { GameObjectEvent } from 'GameObject/GameObject';
import Container from 'Prototype/Container';
import { ResourceDriver } from "Utility/Resource/ResourceManager";
import { StartAnimation, StartAnimationAsync } from '../../Animation/AnimationHelper';
import { PlaySound } from '../../Audio/AudioHelper';
import BaseUIHandler from '../BaseUIHandler';
import BaseUISystem from '../BaseUISystem';
import LeaderboardHelper from '../LeaderboardHelper';
import { applyDefaultButtonAnimation } from '../UIHelper';
import HelperFriend from 'Prototype/Level/HelperFriend';
import { HelperFriendActions } from "State/Level/HelperFriend/HelperFriendActions";
import { HelperFriendStatus } from "State/Level/HelperFriend/HelperFriendStatus";
import { LevelStatus } from "State/Level/LevelStatus";
import SpriteComponent from 'Component/Render/Sprite';
import CardSystem from '../../Card/CardSystem';
import { FBTrackingEvent } from "Utility/Tracking/FBTrackingEvent";
import { IGameState } from "State/Game/IGameState";
import { PopupID } from "State/UI/PopupID";
import { LevelHud } from 'System/UI/HUD/LevelHud';

/* prettier-ignore */ const HELPER_FRIEND_GROUP = 'helper_friend_group';
/* prettier-ignore */ const HELPER_FRIEND_CONTAINER = HELPER_FRIEND_GROUP + '/helper_friend_container';
/* prettier-ignore */ const HELPER_FRIEND_BUTTON = HELPER_FRIEND_CONTAINER + '/accept_button';

export default class HelperFriendHUDHandler extends BaseUIHandler {
    private enabled: boolean = true;
    private btnAcceptHelp: GameObject;
    private helperFriendAnim: GameObject;

    /**
     * The current status of the helper-friend component so as not to re-trigger the animations
     * in `handleStateChanged()`.
     * TODO: Refactor so the handler does not need to keep track of the components status.
     */
    private helperFriendStatus: HelperFriendStatus;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.HELPER_FRIEND_HUD, uiSystem);

        this.alwaysVisible = true;

        // Load sounds in the background
        this.secondaryResources.set(
            'helper_friend_appear',
            'resource/audio/helperfriend/helper_friend_appear.mp3'
        );
        this.secondaryResources.set(
            'helper_friend_dismiss',
            'resource/audio/helperfriend/helper_friend_dismiss.mp3'
        );
    }

    public handleStateChanged(state: IGameState) {
        if (
            state.levelState.status !== LevelStatus.INTRO &&
            state.levelState.status !== LevelStatus.ACTIVE &&
            state.levelState.status !== LevelStatus.COMPLETE
        ) {
            return;
        }

        // Check if helperFriendStatus has changed.
        if (this.helperFriendStatus !== state.levelState.helperFriendStatus) {
            this.helperFriendStatus = state.levelState.helperFriendStatus;
            switch (this.helperFriendStatus) {
                case HelperFriendStatus.ENABLED:
                case HelperFriendStatus.DISABLED:
                    this.enableInteractables(false);
                    this.dismissHelperFriend();
                    break;
                case HelperFriendStatus.SHOW:
                    this.showHelperFriend();
                    this.enableInteractables(
                        state.levelState.status === LevelStatus.ACTIVE &&
                            this.enabled
                    );
                    break;
                case HelperFriendStatus.USED:
                    this.enableInteractables(false);
                    this.useHelperFriend();
                    break;
            }
        }
    }

    protected onCreate() {
        super.onCreate();
        this.system.scene.on('ToggleLevelHUD', (toggle: boolean) => {
            this.enabled = toggle;

            // TODO: Line may not be necessary.
            this.handleStateChanged(Game.store.getState());
        });

        this.createHelperFriend();
    }

    protected enableInteractables(value: boolean): void {
        if (!this.helperFriendAnim) return;

        this.btnAcceptHelp.getComponent(Interactable).enabled = value;
    }

    private useHelperFriend() {
        if (!this.helperFriendAnim) return;

        this.helperFriendAnim.getComponent(Renderable).visible = true;

        const cardSystem: CardSystem = this.system.scene.getSystem(CardSystem);
        const sourceCard = GameObject.Instantiate(Container);
        this.helperFriendAnim.addChild(sourceCard);
        sourceCard.container.position.set(
            0,
            -this.helperFriendAnim.container.height / 2
        );
        StartAnimationAsync(this.helperFriendAnim, 'HelperPlusCards', {
            sourceCard,
            stackContainer: cardSystem.stackContainer,
            system: cardSystem,
        }).then(() => {
            this.dismissHelperFriend();
            cardSystem.cardStack.update();
        });
    }

    private dismissHelperFriend() {
        if (!this.helperFriendAnim) return;

        /*
        PlaySound('helper_friend_dismiss');
        */

        StartAnimationAsync(this.helperFriendAnim, 'Alpha', {
            startAlpha: 1,
            endAlpha: 0,
            duration: 150,
        }).then(() => {
            this.setHelperFriendVisible(false);
        });
    }

    private setHelperFriendVisible(visible: boolean) {
        this.helperFriendAnim.getComponent(Renderable).visible = visible;
    }

    private showHelperFriend() {
        if (!this.helperFriendAnim) return;

        this.helperFriendAnim.getComponent(Renderable).visible = true;

        PlaySound('helper_friend_appear');

        this.setHelperFriendVisible(true);

        StartAnimationAsync(this.helperFriendAnim, 'Alpha', {
            startAlpha: 0,
            endAlpha: 1,
            duration: 350,
        }).then(() => {
            StartAnimation(this.helperFriendAnim, 'HelperFriendLoop');
        });

        Game.sdk.gameTracking(FBTrackingEvent.Helper_Show);
    }

    private async createHelperFriend() {
        // Check if helper-friend component instance already exists.
        if (this.helperFriendAnim !== undefined) return;

        // TODO: Move 'platform friends' to state for consistency and testability.
        const friend = await LeaderboardHelper.getFriendForHelp();

        // If no friends available then hide this component.
        if (!friend) {
            this.enabled = false;
            this.getContainer().getComponent(Renderable).visible = false;
            return;
        }

        this.element.find(HELPER_FRIEND_GROUP).container.visible = true;

        this.helperFriendAnim = GameObject.Instantiate(HelperFriend);
        this.btnAcceptHelp = this.helperFriendAnim.findChildByName(
            HelperFriend.BUTTON
        );

        const avatarResourceId = LeaderboardHelper.getPlayerAvatarId(
            friend.user.playerId,
            false
        );

        Game.resource.add(
            avatarResourceId,
            friend.user.avatarUrl,
            ResourceDriver.TERTIARY
        );
        Game.resource
            .wait(avatarResourceId)
            .then(() => {
                if (!this.helperFriendAnim || this.helperFriendAnim.destroyed)
                    return;

                const avatar: GameObject = this.helperFriendAnim.findChildByName(
                    HelperFriend.AVATAR
                );
                const spriteComponent: SpriteComponent = avatar.getComponent(
                    SpriteComponent
                );
                spriteComponent.texture = avatarResourceId;
                spriteComponent.width = spriteComponent.height = 59;
            })
            .catch(e => {
                console.log(e);
                // TODO: Display no avatar icon
                // Avatar failed to load - do nothing.
            });

        const helperFriendContainer = this.getContainer();

        if (!helperFriendContainer) return;

        helperFriendContainer.addChild(this.helperFriendAnim);
        this.helperFriendAnim.container.position.set(-300, -12);

        this.onSelected = this.onSelected.bind(this, friend.user.playerId);
        this.helperFriendAnim.on(GameObjectEvent.SELECTED, this.onSelected);
        this.btnAcceptHelp.on(GameObjectEvent.SELECTED, this.onSelected);

        applyDefaultButtonAnimation(this.btnAcceptHelp, 'button-press');

        this.setHelperFriendVisible(false);

        // Call handleStateChanged again due to async loading of helper-friend.
        this.handleStateChanged(Game.store.getState());
    }

    private onSelected(friendId: string) {
        Game.sdk.gameTracking(FBTrackingEvent.Helper_Accept);
        HelperFriendActions.helperFriendRequest(friendId);
    }

    private getContainer(): Container {
        const helperFriendContainers = GameObject.findByName(
            'helperFriendContainer'
        );

        if (helperFriendContainers && helperFriendContainers.length > 0)
            return helperFriendContainers[0] as Container;

        // Add as a child to 'stackContainer' in card-system for relative positioning.
        const container = (LevelHud.instance.getLayout() as any).centerBottom;

        const helperFriendContainer = GameObject.Instantiate(Container, {
            name: 'helperFriendContainer',
        });

        // Send helper-friends to back of `stackOuterContainer`
        container.addChildAt(helperFriendContainer, 0);

        return helperFriendContainer;
    }
}
